
/**
 * Module dependencies.
 */

import { Fill } from 'react-components/layout';
import { FocusEvent, MouseEvent, ReactNode, useEffect, useRef } from 'react';
import { Icon } from 'react-components/media';
import { IconButton } from '../buttons/icon-button';
import { ModalBackdrop } from './modal-backdrop';
import { Text } from '../typography';
import { color, media } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { useBreakpoint } from '../../breakpoint';
import { useTranslate } from '../../i18n';
import closeIcon from '../assets/svg/24/close.svg';
import styled, { css } from 'styled-components';

/**
 * Export `ModalProps` type.
 */

export type ModalProps = {
  isVisible: boolean;
  onRequestClose: (event?: KeyboardEvent | Event) => void;
};

/**
 * `Props` type.
 */

type Props = ModalProps & {
  children?: ReactNode;
  className?: string;
  closeOnPressBackdrop?: boolean;
  description?: ReactNode;
  desktopIcon?: string;
  iconColor?: string | (() => void);
  mobileIcon?: string;
  onFocus?: (event: FocusEvent<HTMLDivElement>) => void;
  title?: ReactNode;
};

/**
 * `ModalContent` styled component.
 */

const ModalContent = styled(Fill)<{ isVisible: boolean }>`
  align-items: center;
  background-color: transparent;
  display: flex;
  justify-content: center;
  outline: none;
  overflow: auto;
  padding: 16px;
  position: fixed;
  transition: visibility ${theme('animations.quartTransition')};
  z-index: ${theme('zIndex.modal.container')};

  ${ifProp('isVisible', css`
    animation: ${theme('keyframes.modalFadeInUp')} ${theme('animations.quartTransition')} both;
    visibility: visible;
  `, css`
    animation: ${theme('keyframes.fadeOut')} ${theme('animations.defaultTransition')} both;
    pointer-events: none;
    visibility: hidden;
  `)}
`;

/**
 * `ModalBody` styled component.
 */

const ModalBody = styled.div`
  background-color: ${color('white')};
  border-radius: ${theme('dimensions.borderRadius')}px;
  box-shadow: ${theme('boxShadow.modal')};
  display: grid;
  grid-template-columns: minmax(auto, 436px);
  margin: auto;
  padding: 16px 16px 24px;

  ${media.min('md')`
    padding-bottom: 56px;
  `}
`;

/**
 * `ModalBodyContent` styled component.
 */

const ModalBodyContent = styled.div`
  ${media.min('md')`
    padding-left: 16px;
    padding-right: 16px;
  `}
`;

/**
 * `StyledIconButton` styled component.
 */

const StyledIconButton = styled(IconButton)`
  margin-left: auto;
`;

/**
 * `StyledIcon` styled component.
 */

const StyledIcon = styled(Icon)`
  display: block;
  margin: 16px auto 0;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text).attrs({ as: 'h2', size: 'large' })` 
  color: ${color('secondary')};
  font-weight: 700;
  margin: 24px 0 8px;
  text-align: center;
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text).attrs({ size: 'normal' })`
  color: ${color('gray500')};
  margin-bottom: 32px;
  text-align: center;

  ${media.min('md')`
    margin-bottom: 48px;
  `}
`;

/**
 * Export `Modal` component.
 */

export function Modal(props: Props): JSX.Element {
  const {
    children,
    className,
    closeOnPressBackdrop = false,
    description,
    desktopIcon,
    iconColor,
    isVisible,
    mobileIcon,
    onFocus,
    onRequestClose,
    title
  } = props;

  const { translate } = useTranslate();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const isDesktop = useBreakpoint('md', 'min');

  useEffect(() => {
    if (isVisible) {
      const timeout = setTimeout(() => {
        if (modalRef?.current) {
          modalRef.current.focus();
        }
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isVisible]);

  useEffect(() => {
    const documentRoot = document.getElementById('root');

    if (!documentRoot) {
      return;
    }

    if (isVisible) {
      documentRoot.setAttribute('aria-hidden', 'true');
    } else {
      documentRoot.removeAttribute('aria-hidden');
    }
  }, [isVisible]);

  return (
    <>
      <ModalBackdrop
        isClickable={closeOnPressBackdrop}
        isVisible={isVisible}
        onRequestClose={onRequestClose}
      />

      <ModalContent
        aria-hidden={!isVisible}
        aria-modal
        isVisible={isVisible}
        onFocus={onFocus}
        ref={modalRef}
        role={'dialog'}
        tabIndex={isVisible ? -1 : undefined}
      >
        <ModalBody className={className}>
          <StyledIconButton
            aria-label={translate('common.actions.close')}
            colorTheme={'transparent'}
            icon={closeIcon}
            iconSize={'24px'}
            onClick={(event: MouseEvent<HTMLButtonElement>) => onRequestClose(event.nativeEvent)}
            size={'24px'}
          />

          <ModalBodyContent>
            {desktopIcon && mobileIcon && (
              <StyledIcon
                aria-hidden
                color={iconColor ?? color('gray500')}
                icon={isDesktop ? desktopIcon : mobileIcon}
                size={isDesktop ? '72px' : '40px'}
              />
            )}

            {title && (
              <Title>
                {title}
              </Title>
            )}

            {description && (
              <Description>
                {description}
              </Description>
            )}

            {children}
          </ModalBodyContent>
        </ModalBody>
      </ModalContent>
    </>
  );
}
